import { Controller } from "@hotwired/stimulus"

import ApexCharts from "apexcharts"
import Rails from "@rails/ujs"

import { showElement } from "../src/dom"
import { round } from "../src/math"

const GRANULARITY_DAY = "day"
const GRANULARITY_MONTH = "month"

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
]

const prepareChartData = (response) => {
  const chartData = {
    hits: [],
    plays: [],
    labels: []
  }

  response.data.forEach(day => {
    chartData.hits.push(day.hits)
    chartData.plays.push(day.plays)

    if (response.granularity === GRANULARITY_DAY) {
      chartData.labels.push(day.date)
    } else {
      const parts = day.date.split("-")
      chartData.labels.push(`${MONTH_NAMES[parts[1] - 1]} ${parts[0]}`)
    }
  })

  return chartData
}

export default class extends Controller {
  static targets = [
    // Query data
    "dateSelection",
    "projectSelection",
    // Visuals
    "chart",
    "numberOfPlays",
    "numberOfViews",
    "playPercentage",
    "visualsContainer"
  ]

  static values = {
    // Configuration
    dataUrl: String,
    // Initial data supplied through params
    endDate: String,
    startDate: String,
    projectSlug: String,
  }

  // Lifecycle

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) { return }

    this.currentProjectSlug = this.projectSlugValue
    this.currentStartDate = this.startDateValue
    this.currentEndDate = this.endDateValue

    this.fetchData()
  }

  disconnect() {
    if (this.instance) { this.instance.destroy() }
  }

  // Handlers

  fetchData() {
    if (!this.hasAnySelectionChanged()) { return }

    const selectedDates = this.selectedDates
    this.currentProjectSlug = this.selectedProjectSlug
    // These are no longer formatted in the German style, but that doesn't matter. Rails will parse them properly.
    this.currentStartDate = selectedDates[0]
    this.currentEndDate = selectedDates[1]

    const params = new URLSearchParams({
      project_slug: this.currentProjectSlug,
      start_date: this.currentStartDate,
      end_date: this.currentEndDate
    })

    const dataUrl = `${this.dataUrlValue}?${params.toString()}`
    const shareableUrl = `${window.location.pathname}?${params.toString()}`

    Rails.ajax({
      type: "GET",
      url: dataUrl,
      success: (response) => {
        history.replaceState({}, "", shareableUrl)
        this.updateVisuals(response)
      },
      error: (response) => { this.showErrorMessage() }
    })
  }

  // Actions

  updateVisuals(response) {
    const hits = response.number_of_hits || 0
    const plays = response.number_of_plays || 0
    const percentage = round((plays / hits) * 100, 2)

    this.numberOfViewsTarget.innerText = Intl.NumberFormat("de-DE").format(response.number_of_hits || 0)
    this.numberOfPlaysTarget.innerText = Intl.NumberFormat("de-DE").format(response.number_of_plays || 0)
    this.playPercentageTarget.innerText = isNaN(percentage) ? "-/-" : `${percentage} %`

    this.buildChart(prepareChartData(response))

    this.visualsContainerTargets.forEach(elem => showElement(elem))
  }

  buildChart({ hits, plays, labels }) {
    const chartOptions = {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        height: "100%"
      },
      colors: ["#e77817", "#ff9319", "#ffb118"],
      dataLabels: { enabled: false },
      series: [
        {
          name: "Hits",
          data: hits
        },
        {
          name: "Plays",
          data: plays
        }
      ],
      xaxis: {
        categories: labels
      },
      yaxis: {
        labels: {
          formatter: function (value) { return Intl.NumberFormat("de-DE").format(value || 0) }
        }
      }
    }

    if (this.chartInstance) {
      this.chartInstance.updateOptions(chartOptions)
    } else {
      this.chartInstance = new ApexCharts(this.chartTarget, chartOptions)
      this.chartInstance.render()
    }
  }

  showErrorMessage() {
    console.error("Failed to load data :<")
  }

  // Predicates

  hasAnySelectionChanged() {
    const selectedDates = this.selectedDates

    if (this.selectedProjectSlug != this.currentProjectSlug) { return true }
    if (selectedDates[0] !== this.currentStartDate) { return true }
    if (selectedDates[1] !== this.currentEndDate) { return true }

    return false
  }

  // Getters

  get selectedProjectSlug() {
    if (!this.hasProjectSelectionTarget) { return }

    return this.projectSelectionTarget.value
  }

  get selectedDates() {
    if (!this.hasDateSelectionTarget) { return }

    // This will get called before the Datepicker component had time to initialize, so we fall back to the dates provided by the server.
    const dateSelection = this.dateSelectionTarget.value || `${this.currentStartDate} - ${this.currentEndDate}`

    return dateSelection
      .split(" - ")
      .map(germanFormattedString => {
        const parts = germanFormattedString.trim().split(".")
        return `${parts[2]}-${parts[1]}-${parts[0]}`
      })
  }
}
